import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AuthTokenService } from '@core/services/auth-token.service';
import { UserStore } from '@core/stores/user.store';

@Injectable({ providedIn: 'root' })
export class AuthChildGuard implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly authTokenService = inject(AuthTokenService);
  private readonly messageService = inject(MessageService);
  private readonly userStore = inject(UserStore);
  private readonly serviceProviderContractRoute = '/io-coins/firmar-contrato';
  private readonly allowedContractPendingRoutes = [
    this.serviceProviderContractRoute,
    '/terminos-y-condiciones',
  ];

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | UrlTree {
    return of(this.authTokenService.getTokenData()).pipe(
      switchMap(async token => {
        const returnUrl = state.url;

        if (!token) {
          this.authTokenService.deleteAll();
          return this.goToLogin(returnUrl);
        }

        if (this.authTokenService.isTokenExpired()) {
          this.authTokenService.deleteAll();
          await this.showMessage();
          return this.goToLogin(returnUrl);
        }

        if (
          this.userStore.mustSignServiceProviderContract() &&
          !this.allowedContractPendingRoutes.includes(returnUrl)
        ) {
          return this.router.createUrlTree([this.serviceProviderContractRoute]);
        }

        return true;
      })
    );
  }

  goToLogin(returnUrl: string) {
    return this.router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }

  async showMessage() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Su sesión ha expirado.',
      detail: 'Por favor, vuelva a iniciar sesión.',
      life: 5000,
    });
  }
}
